import { useCallback, useEffect, useState } from 'react';
import { LanguageProvider } from './i18n/LanguageContext';
import type { MissionId } from './data/missions';
import BackgroundEffects from './components/BackgroundEffects';
import Header from './components/Header';
import Hero from './components/Hero';
import MissionSelector from './components/MissionSelector';
import FeatureStrip from './components/FeatureStrip';
import FeaturedProjects from './components/FeaturedProjects';
import Footer from './components/Footer';
import TaaBot from './components/TaaBot';
import CompeteDashboard from './compete/components/CompeteDashboard';
import CompetitionPage from './compete/components/CompetitionPage';

/** Tracks `window.location.hash`, updating on navigation. */
function useHashRoute(): string {
  const [hash, setHash] = useState(() => window.location.hash);
  useEffect(() => {
    const onChange = () => setHash(window.location.hash);
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return hash;
}

export default function App() {
  const [selectedMission, setSelectedMission] = useState<MissionId>('learn');
  const hash = useHashRoute();

  /** Select a mission: jump to the Taa Arena for Compete, otherwise scroll to the mission section. */
  const selectAndScroll = useCallback((id: MissionId) => {
    if (id === 'compete') {
      window.location.hash = '#/compete';
      return;
    }
    if (id === 'learn') {
      window.location.href = '/src/client/pages/dashboard.html';
      return;
    }
    setSelectedMission(id);
    document.getElementById('missions')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const competeMatch = hash.match(/^#\/compete\/?([\w-]*)/);

  return (
    <LanguageProvider>
      <div className="relative min-h-screen overflow-x-clip bg-navy-950 text-slate-200">
        <BackgroundEffects />
        <Header onSelectMission={selectAndScroll} />
        <main className="relative z-10">
          {competeMatch ? (
            competeMatch[1] ? <CompetitionPage id={competeMatch[1]} /> : <CompeteDashboard />
          ) : (
            <>
              <Hero />
              <MissionSelector selected={selectedMission} onSelect={setSelectedMission} />
              <FeatureStrip />
              <FeaturedProjects />
            </>
          )}
        </main>
        <Footer />
        <TaaBot onSelectMission={selectAndScroll} />
      </div>
    </LanguageProvider>
  );
}
